import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Lock, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BRAND } from "@/lib/constants";

export default function AdminLoginPage() {
    const { user, login, loading } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    if (!loading && user && user.role === "admin") {
        return <Navigate to="/admin" replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await login(email.trim().toLowerCase(), password);
            toast.success("Welcome back.");
            navigate("/admin");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6" data-testid="admin-login-page">
            <div className="absolute inset-0 spark-pattern opacity-20 pointer-events-none" />
            <div className="hero-glow left-1/2 top-10 -translate-x-1/2" />
            <form onSubmit={onSubmit} className="relative card-surface p-8 w-full max-w-md reveal">
                <div className="flex items-center gap-2">
                    <span className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/40 inline-flex items-center justify-center text-emerald-300">
                        <Zap className="w-5 h-5" />
                    </span>
                    <span className="font-extrabold text-white text-lg">{BRAND}</span>
                </div>
                <h1 className="mt-6 text-2xl font-extrabold text-white">Admin sign in</h1>
                <p className="text-sm text-slate-400 mt-1">Access the leads dashboard.</p>
                <div className="mt-6 space-y-4">
                    <div>
                        <label className="text-xs text-slate-400">Email</label>
                        <input
                            data-testid="admin-email-input"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            inputMode="email"
                            className="input-dark mt-1"
                            placeholder="you@example.com"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-slate-400">Password</label>
                        <input
                            data-testid="admin-password-input"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-dark mt-1"
                            placeholder="••••••••"
                        />
                    </div>
                </div>
                <button
                    type="submit"
                    disabled={submitting || !email || !password}
                    data-testid="admin-login-btn"
                    className="btn-primary mt-6 w-full disabled:opacity-50"
                >
                    <Lock className="w-4 h-4" /> {submitting ? "Signing in..." : "Sign in"}
                </button>
            </form>
        </div>
    );
}
